import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {DEFAULT_LINE_PLOT_MAX_GROUP_RUNS} from '../../components/WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {IconPositioningContextProvider} from '../../state/issues/IconPositioningContext';
import {type IssueLine} from '../../state/issues/types';
import {prettyXAxisLabel} from '../../util/plotHelpers/axis';
import {
  getPlotFontSize,
  plotFontSizeToPx,
} from '../../util/plotHelpers/plotFontSize';
import {convertScalarSecondsToTimestep} from '../../util/plotHelpers/time';
import {type Line} from '../../util/plotHelpers/types';
import {useResizeObserverDebounced} from '../../util/useResizeObserverDebounced';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {PanelTitle} from '../PanelTitle/PanelTitle';
import {type BrushEndMetadata} from '../vis/highlight';
import {LinePlot} from '../vis/LinePlot';
import {type DomainMaybe} from '../vis/types';
import {logHandledError} from './../../services/errors/errorReporting';
import {BarChart} from './BarChart';
import {getTitleFromConfig} from './common';
import {getErrorMessage} from './getGraphError';
import {PlotLayout} from './Layout';
import {LinePlotLegend} from './LinePlotLegend';
import {LinePlotLegendWarning} from './LinePlotLegendWarning';
import {MissingRunBanner} from './MissingRunBanner';
import {
  getZoomUsageType,
  useLinePlotZoomUsageTracker,
} from './observability/zoomUsage';
import {usePanelConfigContext} from './PanelConfigContext';
import {usePanelInteractionContext} from './PanelInteractionContext';
import {usePanelTimeContext} from './PanelTimeContext';
import {usePanelZoom} from './PanelZoomContext';
import {SamplingWarning} from './SamplingWarning';
import {useSharedPanelZoom} from './SharedPanelZoomContext';
import {usePanelSystemMetricsContext} from './SystemMetrics/PanelSystemMetricsContext';
import {
  type RunsLinePlotPanelInnerProps,
  type UserZoomIntentPayload,
} from './types';
import {useLegendVisibility} from './useLegendVisibility';
import {useLinesForDisplay} from './useLinesForDisplay';
import {
  linesHaveRenderableData,
  useRegexMetricSettlingLoading,
} from './useRegexMetricSettlingLoading';
import {getDomain} from './utils';
import {isBarPlot} from './utils/checkTypes';
import {usePanelZoom2} from './zoom/PanelZoomContext2';
import {
  getZoomIntent,
  isResetZoomIntent,
  shouldPropagateSharedXAxisZoom,
} from './zoom/zoomIntent';

type GraphProps = RunsLinePlotPanelInnerProps & {
  issueLines?: IssueLine[];
};

const expandStyles: React.CSSProperties = {
  height: '100%',
  width: '100%',
};

const SAMPLING_WARNING_STYLE: React.CSSProperties = {zIndex: 1};

/**
 * Highest per-runset run count — the signal the multi-runset warning path
 * keys off of. `Math.max(...[])` is `-Infinity`, so guard on empty map.
 */
function getMaxFetchedRunCount(
  countsByRunsetId: Record<string, number> | undefined
): number {
  const values = Object.values(countsByRunsetId ?? {});
  if (values.length === 0) {
    return 0;
  }
  return Math.max(...values);
}

const GraphComp: React.FC<GraphProps> = props => {
  'use memo';

  // destructure
  const {
    lines,
    config,
    issueLines = [],
    loading,
    runNameTruncationType,
  } = props;

  // state
  const [isHovered, setIsHovered] = useState(false);

  const {
    isResizing,
    height = 1,
    ref: elementRef,
    width = 1,
  } = useResizeObserverDebounced<HTMLDivElement>(1);
  const {
    excludeOutliers,
    isFullFidelity,
    isMultiMetric,
    singlePointRenderMode,
    showLegend,
    xAxis,
    xLogScale,
    xAxisIsDatetime,
    yLogScale,
  } = usePanelConfigContext();
  const {setTimeFactor} = usePanelTimeContext();
  const {setZooming, zooming: zooming1} = usePanelInteractionContext();
  const {isDelayedSystemMetrics} = usePanelSystemMetricsContext();
  const {
    handleUserZoomChange,
    xDomainChart,
    yDomain: yDomainOld,
  } = usePanelZoom();
  const {handleUserZoom, queryZoom, zooming: zooming2} = usePanelZoom2();
  const {setSharedZoom} = useSharedPanelZoom();

  const updateUserZoom = isFullFidelity ? handleUserZoom : handleUserZoomChange;
  const yDomain = isFullFidelity
    ? getDomain(queryZoom.yAxisMin, queryZoom.yAxisMax)
    : yDomainOld;
  const zooming = isFullFidelity ? zooming2 : zooming1;
  const historySpaceXDomain = isFullFidelity
    ? getDomain(queryZoom.xAxisMin, queryZoom.xAxisMax)
    : getDomain(xDomainChart?.[0] ?? undefined, xDomainChart?.[1] ?? undefined);

  const fontSize = getPlotFontSize(config.fontSize ?? 'auto', height);

  // Additional constraints are applied for legends depending on panel width/height and if
  // view is single run
  const isLegendShown = useLegendVisibility(showLegend, isMultiMetric, {
    height,
    width,
  });

  const timestep = lines?.[0]?.timestep;

  // make sure to convert x domain to the correct time type
  const xDomain = useMemo(() => {
    if (timestep && historySpaceXDomain != null) {
      return [
        historySpaceXDomain[0] == null
          ? historySpaceXDomain[0]
          : convertScalarSecondsToTimestep(historySpaceXDomain[0], timestep),
        historySpaceXDomain[1] == null
          ? historySpaceXDomain[1]
          : convertScalarSecondsToTimestep(historySpaceXDomain[1], timestep),
      ] as DomainMaybe;
    }

    if (historySpaceXDomain != null && xAxisIsDatetime) {
      return [
        historySpaceXDomain[0] == null
          ? historySpaceXDomain[0]
          : historySpaceXDomain[0] * 1000,
        historySpaceXDomain[1] == null
          ? historySpaceXDomain[1]
          : historySpaceXDomain[1] * 1000,
      ] as DomainMaybe;
    }

    return historySpaceXDomain;
  }, [historySpaceXDomain, timestep, xAxisIsDatetime]);

  const xScale = xLogScale ? 'log' : 'linear';
  const yScale = yLogScale ? 'log' : 'linear';

  const {filteredLines} = useLinesForDisplay(lines, {
    xScale,
    yScale,
    isFullFidelity,
    isHovered,
  });
  const isRegexMetricSettling = useRegexMetricSettlingLoading(
    config,
    filteredLines
  );
  const effectiveLoading = loading || isRegexMetricSettling;

  // don't include auxiliary lines in the legend, they're "real" for the purposes of
  // chart rendering but not for the purposes of the legend
  const legendLines = useMemo(
    () => filteredLines.filter(l => !l.aux),
    [filteredLines]
  );

  // Separate lines into visible (has data) and hidden (no data) without mutation
  const {visibleLines, hiddenLines} = useMemo(
    () =>
      filteredLines.reduce<{visibleLines: Line[]; hiddenLines: Line[]}>(
        (acc, line) => {
          const hasData =
            line.data.length > 0 ||
            (line.nanPoints != null && line.nanPoints.length > 0);
          if (hasData) {
            acc.visibleLines.push(line);
          } else {
            acc.hiddenLines.push(line);
          }
          return acc;
        },
        {visibleLines: [], hiddenLines: []}
      ),
    [filteredLines]
  );
  const trackZoomUsage = useLinePlotZoomUsageTracker({
    lines: visibleLines,
    metrics: config.metrics,
    xAxis,
  });
  const activeZoom =
    (Array.isArray(xDomain) && (xDomain[0] != null || xDomain[1] != null)) ||
    (Array.isArray(yDomain) && (yDomain[0] != null || yDomain[1] != null));

  const sharedZoomCallback = useCallback(
    (zoom: UserZoomIntentPayload) => {
      if (!shouldPropagateSharedXAxisZoom(zoom)) {
        return;
      }
      setSharedZoom({
        xAxis,
        xMin: zoom.xAxisMin ?? null,
        xMax: zoom.xAxisMax ?? null,
      });
    },
    [xAxis, setSharedZoom]
  );
  /**
   * Notes on timestep:
   * 1. Timestep is determined by examining the lines data and determining a proper scale
   * 2. The timestep is saved across the panel through the interaction context
   * 3. when we zoom we don't want to change the timestep factor
   */
  const panelZoomCallback = useCallback(
    (zoom: UserZoomIntentPayload, brushEndMetadata?: BrushEndMetadata) => {
      const intent = getZoomIntent(zoom);
      const usageType = getZoomUsageType(intent);
      if (isResetZoomIntent(zoom)) {
        setTimeFactor(null);
        if (!isFullFidelity) {
          setZooming(false);
        }
      } else {
        if (!isFullFidelity) {
          setZooming(true);
        }
        if (intent.shouldZoomX) {
          setTimeFactor(timestep ?? null);
        }
      }
      updateUserZoom(zoom);

      // Usage telemetry records committed user gestures, not whether zoom state
      // accepted the gesture after config-zoom exclusion rules.
      if (brushEndMetadata != null && (usageType !== 'reset' || activeZoom)) {
        trackZoomUsage({
          type: usageType,
          source: 'drag',
          xZoomPercent: brushEndMetadata.xZoomPercent,
          yZoomPercent: brushEndMetadata.yZoomPercent,
        });
      }
    },
    [
      activeZoom,
      isFullFidelity,
      setTimeFactor,
      setZooming,
      timestep,
      trackZoomUsage,
      updateUserZoom,
    ]
  );
  const zoomCallback = useCallback(
    (zoom: UserZoomIntentPayload, brushEndMetadata?: BrushEndMetadata) => {
      sharedZoomCallback(zoom);
      panelZoomCallback(zoom, brushEndMetadata);
    },
    [sharedZoomCallback, panelZoomCallback]
  );

  // derived values
  const [errorDetail, errorMessage] = getErrorMessage(
    config,
    isDelayedSystemMetrics,
    filteredLines,
    {
      activeZoom,
      zoomCallback,
    }
  );

  const renderBarChart = isBarPlot(props.lines, zooming, singlePointRenderMode);
  const renderPanelError = !effectiveLoading && errorMessage != null;

  useEffect(
    () => {
      if (!!errorDetail && !effectiveLoading) {
        logHandledError(errorDetail as string, {
          extra: {
            metrics: config.metrics?.join(', '),
            xAxis: xAxis ?? '',
          },
        });
      }
    },
    // don't trigger this if the loading changes, ONLY on changes in the message
    // there's the possibility this could thrash if the message changes rapidly
    // from "" to "[some message]" if this is interaction based, but we'll tune
    // if we see this start hammering the logs
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [errorDetail, config.metrics, xAxis]
  );

  const [isDrawing, setIsDrawing] = useState(false);

  const stablePanelId = useStablePanelId();

  const panelErrorMetaInfo = useMemo(
    () => ({isFullFidelity}),
    [isFullFidelity]
  );

  const warning = (
    <LinePlotLegendWarning
      hasSubsampledGroups={props.data.hasSubsampledGroups ?? false}
      isMultiMetric={isMultiMetric ?? false}
      loading={effectiveLoading}
      metrics={config.metrics?.length ?? 0}
      isReadOnly={props.readOnly ?? false}
      lines={legendLines}
      maxFetchedRunCount={getMaxFetchedRunCount(
        props.data.fetchedRunCountByRunsetId
      )}
      maxGroupRuns={config.groupRunsLimit ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS}
      runCount={props.data.fetchedRunCount ?? 0}
      runsetCount={
        Object.keys(props.data.fetchedRunCountByRunsetId ?? {}).length
      }
      totalGroupedIncludedRuns={props.data.totalGroupedIncludedRuns ?? 0}
      disallowSelect={isDrawing}
    />
  );

  if (
    renderBarChart ||
    renderPanelError ||
    (props.hasError && !effectiveLoading)
  ) {
    return (
      <div
        className="chart"
        data-test={`runs-line-plot-error-${
          props.config.xAxis ?? 'unknown-x-axis'
        }`}
        onMouseEnter={() => {
          setIsHovered(true);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
        }}
        ref={elementRef}>
        <PanelTitle title={getTitleFromConfig(config)} fontSize={fontSize} />
        {renderBarChart && warning}
        <div className="chart-content" data-test="chart-content">
          {renderPanelError ? (
            <PanelError
              panelViewType="Run History Line Plot"
              panelID={stablePanelId}
              metaInfo={panelErrorMetaInfo}
              message={
                props.hasError ? (
                  <div>
                    An unexpected error was detected. Will try again shortly.
                  </div>
                ) : (
                  errorMessage
                )
              }
              logErrorMessage={
                props.hasError
                  ? 'An unexpected error was detected. Will try again shortly.'
                  : typeof errorDetail === 'string'
                    ? errorDetail
                    : 'Chart error occurred'
              }
            />
          ) : (
            <BarChart {...props} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      data-x-axis={config.xAxis ?? 'unknown-x-axis'}
      data-test="chart-content"
      ref={elementRef}
      style={expandStyles}>
      <IconPositioningContextProvider issues={issueLines} width={width}>
        <PlotLayout
          fontSize={plotFontSizeToPx.legend[fontSize]}
          handleHover={setIsHovered}
          loading={effectiveLoading}
          numLines={
            linesHaveRenderableData(visibleLines) ? visibleLines.length : 0
          }
          Graph={
            isResizing ? (
              <div />
            ) : (
              <LinePlot
                excludeOutliers={excludeOutliers}
                filteredLines={visibleLines}
                fontSize={fontSize}
                isDrawing={isDrawing}
                isHovered={isHovered}
                issueLines={issueLines}
                runNameTruncationType={runNameTruncationType}
                setIsDrawing={setIsDrawing}
                svg={props.exportMode}
                timestep={timestep}
                xAxis={prettyXAxisLabel(xAxis, props.lines)}
                xAxisTitle={config.xAxisTitle}
                xDomain={xDomain}
                xScale={xScale}
                yAxis={''}
                yAxisTitle={config.yAxisTitle}
                yDomain={yDomain}
                yScale={yLogScale ? 'log' : 'linear'}
                zoomCallback={zoomCallback}
                zooming={zooming}>
                {<MissingRunBanner lines={visibleLines} />}
              </LinePlot>
            )
          }
          legendPosition={config.legendPosition ?? 'north'}
          Legend={
            <LinePlotLegend
              disableRunLinks={props.disableRunLinks}
              expandLegend={props.exportMode}
              fontSize={fontSize}
              hiddenLines={hiddenLines}
              lines={legendLines}
              isVertical={
                !['east', 'west'].includes(config.legendPosition ?? '')
              }
              disallowSelect={isDrawing}
            />
          }
          showLegend={isLegendShown}
          titleText={getTitleFromConfig(config)}
          Title={
            <PanelTitle
              title={getTitleFromConfig(config)}
              fontSize={fontSize}
              disallowSelect={isDrawing}
            />
          }
          Warning={warning}
        />
      </IconPositioningContextProvider>
      <SamplingWarning width={width} style={SAMPLING_WARNING_STYLE} />
    </div>
  );
};
const Graph = memo(GraphComp);
Graph.displayName = 'Graph';

// TODO(axel): This wrapper is only here because I'm not confident that some props are guaranteed to exist.
// Find out what's up and kill this wrapper.
const GraphWrapperComp: React.FC<
  RunsLinePlotPanelInnerProps & {
    issueLines?: IssueLine[];
  }
> = function GraphWrapperComp(props) {
  'use memo';

  if (props.data.histories == null) {
    return <p>No Histories</p>;
  }

  return <Graph {...props} />;
};
export const GraphWrapper = memo(GraphWrapperComp);
GraphWrapper.displayName = 'GraphWrapper';
